/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { getUrlSync } from '@msdyn365-commerce/core';
import { SalesInvoiceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { InvoiceErrorComponent } from '@msdyn365-commerce-modules/invoice';
import { getSimpleProductsAction, ProductInput } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InvoiceDetailsHeaderComponent } from './components/invoice-details-header';
import { InvoiceDetailsPaymentDetailsComponent } from './components/invoice-details-payment-summary';
import { InvoiceDetailsProductsComponent } from './components/invoice-details-products';
import { InvoiceDetailsModel } from './helpers/invoice-data';
import { IInvoiceDetailsData } from './invoice-details.data';
import { IInvoiceDetailsProps } from './invoice-details.props.autogenerated';

export interface IInvoiceDetailsViewState {
    invoiceDetails?: InvoiceDetailsModel;
    payInvoiceErrorModalOpen?: boolean;
}

export interface IInvoiceDetailsViewProps extends IInvoiceDetailsProps<IInvoiceDetailsData> {
    moduleProps: IModuleProps;
    header: JSX.Element;
    products: JSX.Element;
    paymentDetails: JSX.Element;
    payInvoiceError?: JSX.Element;
}

/**
 * InvoiceDetails component.
 * @extends {React.PureComponent<IInvoiceDetailsProps<IInvoiceDetailsData>>}
 */
class InvoiceDetails extends React.PureComponent<IInvoiceDetailsProps<IInvoiceDetailsData>, IInvoiceDetailsViewState> {
    private static readonly _className: string = 'msc-invoice-details';

    public constructor(props: IInvoiceDetailsProps<IInvoiceDetailsData>) {
        super(props);

        this.state = {};
        this._onAddToCartFailed = this._onAddToCartFailed.bind(this);
        this._setPayInvoiceErrorModalClose = this._setPayInvoiceErrorModalClose.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        const invoice = await this.props.data.invoice;
        const invoiceDetails = await this.props.data.invoiceDetails;

        const products = await this._getProducts(invoiceDetails.SalesInvoiceLine, invoiceDetails.ChannelId);

        const invoiceDetailsModel = new InvoiceDetailsModel(invoice, invoiceDetails, products);

        this.setState({
            invoiceDetails: invoiceDetailsModel,
            payInvoiceErrorModalOpen: false
        });
    }

    public render(): JSX.Element | null {
        if (!this.state.invoiceDetails) {
            return null;
        }

        const header = this._renderHeader();
        const products = this._renderProducts();
        const paymentDetails = this._renderPaymentDetails();
        const payInvoiceError = this._renderInvoiceErrors();

        const props: IInvoiceDetailsViewProps = {
            ...(this.props as IInvoiceDetailsProps<IInvoiceDetailsData>),
            moduleProps: {
                moduleProps: this.props,
                className: InvoiceDetails._className
            },
            header,
            products,
            paymentDetails,
            payInvoiceError
        };

        return this.props.renderView(props);
    }

    private async _getProducts(invoiceLines: SalesInvoiceLine[] = [], channelId?: number): Promise<SimpleProduct[]> {
        const productInputs = invoiceLines.filter(product => product.RecordId).map(product => new ProductInput(product.RecordId!, this.props.context.request.apiSettings, channelId));
        return getSimpleProductsAction(productInputs, this.props.context.actionContext);
    }

    private _renderHeader(): JSX.Element {
        const invoiceDetails = this.state.invoiceDetails!;
        return (
            <InvoiceDetailsHeaderComponent
                className={`${InvoiceDetails._className}__header`}
                id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                data={{ invoiceDetails }}
                typeName={this.props.typeName}
                context={this.props.context}
                resources={this.props.resources}
                onError={this._onAddToCartFailed}
            />
        );
    }

    private _renderProducts(): JSX.Element {
        const invoiceDetails = this.state.invoiceDetails!;
        const { imageSettings } = this.props.config;
        return (
            <InvoiceDetailsProductsComponent
                className={`${InvoiceDetails._className}__products`}
                id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                data={{ invoiceDetails }}
                typeName={this.props.typeName}
                context={this.props.context}
                resources={this.props.resources}
                imageSettings={imageSettings}
            />
        );
    }

    private _renderPaymentDetails(): JSX.Element {
        const invoiceDetails = this.state.invoiceDetails!;
        return (
            <InvoiceDetailsPaymentDetailsComponent
                className={`${InvoiceDetails._className}__payment-details`}
                id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                data={{ invoicePaymentDetails: invoiceDetails }}
                typeName={this.props.typeName}
                context={this.props.context}
                resources={this.props.resources}
            />
        );
    }

    private _onAddToCartFailed(): void {
        this.setState({ payInvoiceErrorModalOpen: true });
    }

    private _setPayInvoiceErrorModalClose(): void {
        this.setState({ payInvoiceErrorModalOpen: false });
    }

    private _renderInvoiceErrors(): JSX.Element {
        const {
            resources: { payInvoiceErrorDialogHeader, payInvoiceErrorDialogBodyErrorTitle, payInvoiceErrorDialogBodyErrorDescription,
                payInvoiceErrorDialogFooterGoToCartButtonText, payInvoiceErrorDialogFooterCancelButtonText }
        } = this.props;

        const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';

        const errorClassName = `${InvoiceDetails._className}__errors`;

        return (
            <InvoiceErrorComponent
                id={errorClassName}
                typeName={this.props.typeName}
                data={{}}
                context={this.props.context}
                payInvoiceErrorModalOpen={this.state.payInvoiceErrorModalOpen || false}
                showError={false}
                errorDialogHeader={payInvoiceErrorDialogHeader}
                errorDialogBodyErrorTitle={payInvoiceErrorDialogBodyErrorTitle}
                errorDialogBodyErrorDescription={payInvoiceErrorDialogBodyErrorDescription}
                errorDialogFooterGoToCartButtonText={payInvoiceErrorDialogFooterGoToCartButtonText}
                errorDialogFooterCancelButtonText={payInvoiceErrorDialogFooterCancelButtonText}
                cartUrl={cartUrl}
                setPayInvoiceErrorModalClose={this._setPayInvoiceErrorModalClose}
            />
        );
    }
}

export default InvoiceDetails;
